import {
  AlertTriangle,
  FileText,
  Save,
  X,
} from "lucide-react";
import {
  AutocompleteSelect,
} from "../../../../shared/components/AutocompleteSelect";
import { OfferExtraCostsFields } from "../../../../shared/components/OfferExtraCostsFields";
import {
  calculateOfferTotal,
} from "../../offer-entry";
import type {
  useOffersWorkspace,
} from "./useOffersWorkspace";

export function OfferComposer({ model }: { model: ReturnType<typeof useOffersWorkspace>; }) {
  const {
    composerOpen,
    savingOffer,
    offerFormError,
    editingOfferId,
    offerDocument,
    setOfferDocument,
    extraCostsOpen,
    setExtraCostsOpen,
    requestOptions,
    requestAutocompleteOptions,
    form,
    setForm,
    supplierAutocompleteOptions,
    selectedOfferRequest,
    effectiveEditingOfferId,
    createsNewSupplierOffer,
    deliveryWarning,
    duplicateSupplierOffer,
    closeOfferComposer,
    openOfferEditor,
    saveOffer,
  } = model;
  return (<>
    {composerOpen ? (
      <section
        className="inline-form-card"
        role="dialog"
        aria-modal="true"
        aria-label="Tedarikçi teklifi formu"
      >
        <div className="section-heading compact">
          <div>
            <span className="section-kicker">Ticari kayıt</span>
            <h2>
              {createsNewSupplierOffer
                ? "Yeni tedarikçi teklifi oluştur"
                : editingOfferId
                  ? `${editingOfferId} teklifini düzenle`
                  : "Tedarikçi teklifi ekle"}
            </h2>
          </div>
          <button
            className="icon-button"
            aria-label="Teklif formunu kapat"
            onClick={closeOfferComposer}
          >
            <X size={17} />
          </button>
        </div>
        <div className="inline-form-grid">
          {selectedOfferRequest ? (
            <div className="offer-request-context form-field-wide">
              <span>Planlama talebi</span>
              <strong>
                {form.requestedQuantity || "—"} {form.unit}
              </strong>
              <small>
                {selectedOfferRequest.id} · {selectedOfferRequest.title} ·
                İhtiyaç {selectedOfferRequest.due || "—"}
              </small>
            </div>
          ) : null}
          {offerFormError ? (
            <div className="offer-form-error form-field-wide" role="alert">
              <AlertTriangle size={18} aria-hidden="true" />
              <div>
                <strong>Teklif kaydedilemedi</strong>
                <span>{offerFormError}</span>
              </div>
            </div>
          ) : null}
          <AutocompleteSelect
            label="Satın alma talebi"
            value={form.requestNo}
            options={requestAutocompleteOptions}
            placeholder="Talep numarası veya ürün ara"
            disabled={Boolean(editingOfferId)}
            onChange={(requestNo) => {
              const selectedRequest = requestOptions.find(
                (request) => request.id === requestNo,
              );
              setForm((current) => ({
                ...current,
                requestNo,
                supplierId: "",
                requestedQuantity: selectedRequest?.quantity
                  ? String(selectedRequest.quantity)
                  : "",
                unit: selectedRequest?.unit || "adet",
              }));
            }}
          />
          <AutocompleteSelect
            label="Tedarikçi"
            value={form.supplierId}
            options={supplierAutocompleteOptions}
            placeholder="Seçiniz"
            onChange={(supplierId) =>
              setForm((current) => ({ ...current, supplierId }))
            }
          />
          {createsNewSupplierOffer ? (
            <div className="offer-fork-notice form-field-wide" role="status">
              <AlertTriangle size={18} aria-hidden="true" />
              <div>
                <strong>Yeni teklif oluşturulacak</strong>
                <span>
                  Tedarikçi değiştiği için {editingOfferId} numaralı teklif
                  korunacak ve seçilen tedarikçi adına ikinci bir teklif
                  kaydedilecek.
                </span>
              </div>
            </div>
          ) : null}
          <label>
            Birim fiyat
            <input
              value={form.unitPrice}
              inputMode="decimal"
              placeholder="Örn. 450"
              onChange={(event) =>
                setForm((current) => ({
                  ...current,
                  unitPrice: event.target.value,
                }))
              }
            />
            <small>
              {calculateOfferTotal(form.unitPrice, form.requestedQuantity) !==
                null
                ? `Toplam: ${calculateOfferTotal(form.unitPrice, form.requestedQuantity)?.toLocaleString("tr-TR")} ${form.currency}`
                : "Miktar ve birim fiyat girildiğinde toplam hesaplanır."}
            </small>
          </label>
          <label>
            Para birimi
            <select
              value={form.currency}
              onChange={(event) =>
                setForm((current) => ({
                  ...current,
                  currency: event.target.value,
                }))
              }
            >
              <option>TRY</option>
              <option>EUR</option>
              <option>USD</option>
            </select>
          </label>
          <label>
            Ödeme vadesi (gün)
            <input
              type="number"
              min="0"
              value={form.paymentDays}
              onChange={(event) =>
                setForm((current) => ({
                  ...current,
                  paymentDays: event.target.value,
                }))
              }
            />
          </label>
          <label>
            Teslimat süresi (gün)
            <input
              type="number"
              min="0"
              value={form.deliveryDays}
              onChange={(event) =>
                setForm((current) => ({
                  ...current,
                  deliveryDays: event.target.value,
                }))
              }
            />
          </label>
          <label>
            Geçerlilik tarihi
            <input
              type="date"
              value={form.validUntil}
              onChange={(event) =>
                setForm((current) => ({
                  ...current,
                  validUntil: event.target.value,
                }))
              }
            />
          </label>
          {deliveryWarning ? (
            <div
              className="delivery-date-warning form-field-wide"
              role="status"
            >
              <AlertTriangle size={18} aria-hidden="true" />
              <div>
                <strong>İhtiyaç tarihi aşılacak</strong>
                <span>{deliveryWarning.message}</span>
              </div>
            </div>
          ) : null}
          {duplicateSupplierOffer ? (
            <div
              className="duplicate-offer-warning form-field-wide"
              role="alert"
            >
              <AlertTriangle size={18} aria-hidden="true" />
              <div>
                <strong>Bu tedarikçinin teklifi zaten var</strong>
                <span>
                  {duplicateSupplierOffer.id} numaralı teklifi açıp
                  güncelleyin; aynı talep için ikinci kayıt oluşturulamaz.
                </span>
              </div>
              <button
                className="secondary-button"
                type="button"
                onClick={() => openOfferEditor(duplicateSupplierOffer)}
              >
                Mevcut teklifi düzenle
              </button>
            </div>
          ) : null}
          <OfferExtraCostsFields
            open={extraCostsOpen}
            values={form}
            onToggle={() => setExtraCostsOpen((current) => !current)}
            onChange={(field, value) =>
              setForm((current) => ({
                ...current,
                [field]: value,
              }))
            }
            onDescriptionChange={(value) =>
              setForm((current) => ({
                ...current,
                otherCostDescription: value,
              }))
            }
          />
          <div className="form-field-wide detail-offer-document">
            <span>
              {effectiveEditingOfferId
                ? "Teklife yeni belge ekle"
                : "Teklif belgesi"}
            </span>
            <label className="detail-file-picker">
              <FileText size={18} aria-hidden="true" />
              <span>
                <strong>{offerDocument?.name ?? "Belge seçin"}</strong>
                <small>
                  {offerDocument
                    ? "Seçilen belge teklif kaydıyla birlikte yüklenecek."
                    : effectiveEditingOfferId
                      ? "Mevcut belgeler korunur · En fazla 20 MB"
                      : "PDF, Word, Excel veya görsel · En fazla 20 MB"}
                </small>
              </span>
              <b>{offerDocument ? "Değiştir" : "Dosya seç"}</b>
              <input
                type="file"
                aria-label="Teklif belgesi"
                accept=".pdf,.docx,.xlsx,.png,.jpg,.jpeg"
                onChange={(event) =>
                  setOfferDocument(event.target.files?.[0] ?? null)
                }
              />
            </label>
          </div>
          <label className="form-field-wide">
            Satın alma notu
            <textarea
              rows={3}
              value={form.procurementNote}
              onChange={(event) =>
                setForm((current) => ({
                  ...current,
                  procurementNote: event.target.value,
                }))
              }
            />
          </label>
        </div>
        <div className="inline-form-actions">
          <button className="secondary-button" onClick={closeOfferComposer}>
            Vazgeç
          </button>
          <button
            className="approve-button"
            disabled={savingOffer || Boolean(duplicateSupplierOffer)}
            onClick={saveOffer}
          >
            <Save size={15} />
            {savingOffer
              ? "Kaydediliyor"
              : createsNewSupplierOffer
                ? "Yeni teklif olarak kaydet"
                : editingOfferId
                  ? "Değişiklikleri kaydet"
                  : "Teklifi kaydet"}
          </button>
        </div>
      </section>
    ) : null}
  </>);
}
